import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import StepUpPanel from "../../components/mfa/StepUpPanel";
import PolicyForm from "../../components/mfa/PolicyForm";
import { mfaErr } from "../../lib/webauthn";

const pick = (s) => ({ enrollment_required: !!s.enrollment_required, grace_days: s.grace_days ?? 14, required_roles: s.required_roles || [] });
const summary = (s) => (s.enrollment_required
  ? `On since ${new Date(s.enabled_at).toLocaleDateString()} · ${s.grace_days}-day grace · ${s.required_roles.length} role(s) required`
  : "Off. Nobody is required to use two-step sign-in; people who set it up still use it.");

// Founder Settings → Sign-in security policy. Changing it needs a fresh step-up; every change is audited server-side.
export default function SignInSecurityPolicy() {
  const [status, setStatus] = useState(null);
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [roles, setRoles] = useState([]);
  const [stepUp, setStepUp] = useState(null);
  const [msg, setMsg] = useState({ ok: "", err: "" });
  useEffect(() => {
    api.get("/mfa/status").then((r) => setStatus(r.data)).catch((e) => setMsg({ ok: "", err: mfaErr(e) }));
    api.get("/mfa/settings").then((r) => { setSaved(r.data); setForm(pick(r.data)); }).catch((e) => setMsg({ ok: "", err: mfaErr(e) }));
    api.get("/mfa/settings/role-options").then((r) => setRoles(r.data)).catch(() => setRoles([]));
  }, []);
  const hasFactor = !!status && (status.passkeys.length > 0 || status.totp.status === "active");
  const token = stepUp && stepUp.until > Date.now() ? stepUp.token : null;
  const save = async () => {
    setMsg({ ok: "", err: "" });
    try {
      const r = await api.put("/mfa/settings", form, { headers: { "X-MFA-StepUp": token } });
      setSaved(r.data); setForm(pick(r.data)); setMsg({ ok: "Saved. The change is recorded in the audit log.", err: "" });
    } catch (e) { setMsg({ ok: "", err: mfaErr(e) }); }
  };
  return (
    <AppShell title="Sign-in security policy" subtitle="Who must use two-step sign-in, and how long they have to set it up">
      <div className="mx-auto w-full max-w-[860px] px-4 md:px-6 space-y-4" data-testid="mfa-policy-page">
        {msg.err && <p role="alert" className="text-sm text-red-700" data-testid="mfa-policy-error">{msg.err}</p>}
        {msg.ok && <p role="status" className="text-sm text-green-800" data-testid="mfa-policy-saved">{msg.ok}</p>}
        {!form && !msg.err && <p className="font-mono text-xs" data-testid="mfa-policy-loading">Loading…</p>}
        {saved && <p className="text-sm" data-testid="mfa-policy-summary">Current policy: {summary(saved)}</p>}
        {status && !hasFactor && (
          <p className="text-sm bg-erf-cream border border-erf-borderCream rounded p-3" data-testid="mfa-policy-needs-factor">
            Set up your own passkey or authenticator app first. Changing this policy needs you to confirm it's you.{" "}
            <Link to="/settings/security" className="underline inline-flex min-h-[44px] items-center" data-testid="mfa-policy-setup-link">Go to Sign-in security</Link>
          </p>
        )}
        {status && hasFactor && <StepUpPanel status={status} stepUp={stepUp} setStepUp={setStepUp} />}
        {form && <PolicyForm form={form} setForm={setForm} roles={roles} disabled={!token} />}
        {form && <Button type="button" onClick={save} disabled={!token} className="min-h-[44px] bg-erf-gold text-erf-dark font-semibold" data-testid="mfa-policy-save">Save policy</Button>}
      </div>
    </AppShell>
  );
}
